import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, AlertTriangle } from 'lucide-react';
import { MedicalRecordListResponse } from '../../types/medicalRecord';
import { API_ENDPOINTS } from '../../config/api';
import { getAuthHeaders } from '../../utils/authUtils';

interface MedicalRecordHistoryProps {
  petId?: string;
}

const MedicalRecordHistory: React.FC<MedicalRecordHistoryProps> = ({ petId: propPetId }) => {
  const params = useParams<{ petId: string }>();
  const petId = propPetId || params.petId;
  const navigate = useNavigate();

  const [history, setHistory] = useState<MedicalRecordListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (petId) {
      fetchHistory();
    }
  }, [petId]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_ENDPOINTS.MEDICAL_RECORDS.HISTORY(petId!), {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied to view medical summary history');
        }
        throw new Error('Failed to fetch medical summary history');
      }

      const historyData = await response.json();
      setHistory(historyData);
    } catch (err) {
      console.error('Error fetching medical summary history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-600 dark:text-dark-text-muted">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-none p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* History Table */}
      <div className="bg-white dark:bg-dark-bg-secondary rounded-none shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
        {!history || history.records.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-dark-text-muted">
            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p>No history available.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 dark:bg-dark-bg-tertiary border-b border-gray-200 dark:border-dark-border">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase">
                    Version
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase">
                    Date
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.records.map((record) => (
                  <tr
                    key={record.id}
                    onClick={() => navigate(`/dashboard/pets/${petId}/medical-records/${record.id}`)}
                    className={`border-b border-gray-100 dark:border-dark-border cursor-pointer transition-colors ${
                      record.is_current 
                        ? 'bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-100 dark:hover:bg-blue-900/20' 
                        : 'hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                    }`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                      v{record.version}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-dark-text-secondary">
                      {formatDate(record.visit_date)}
                    </td>
                    <td className="px-4 py-3">
                      {record.is_current ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
                          Current
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-dark-text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicalRecordHistory;
